import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/FileDownload';
import UploadIcon from '@mui/icons-material/FileUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Paper from '../../apiForm/panel/Paper';
import { downloadFile, readFile } from './downloadUtils';
import PromotionsIcon from '../../icons/redeem.svg?react';
import { addHost } from '../../utils/urls';
import UploadDialog from './FileUpload/UploadDialog';
import countRulesInGroups from './promotionUtils';
import useExportApi from './useExportApi';

export default function PromotionsExport() {
  const { t } = useTranslation();
  const api = useExportApi();
  const { projectId } = useParams();

  const rpLink = addHost(
    document.location,
    `/${projectId}/promotions/promotionRules`,
    'retailer',
  );

  const [downloading, setDownloading] = useState(false);
  const [downloadedPromotionsCount, setDownloadedPromotionsCount] = useState<
    number | undefined
  >();

  const [uploading, setUploading] = useState(false);
  const [uploadingDialog, setUploadingDialog] = useState(false);
  const [uploadedPromotionsCount, setUploadedPromotionsCount] = useState<
    number | undefined
  >();

  const handlePromotionsDownload = useCallback(async () => {
    setDownloading(true);
    const data = await api.getPromotions(null);
    if (!data) {
      setDownloadedPromotionsCount(0);
      setDownloading(false);
      return;
    }
    setDownloadedPromotionsCount(countRulesInGroups(JSON.parse(data)));
    downloadFile(
      data,
      `${projectId}-promotion-export.json`,
      'application/json',
    );
    setDownloading(false);
  }, [api, projectId]);

  const handlePromotionsUpload = async (file: File) => {
    setUploadingDialog(false);
    setUploading(true);
    const data = await readFile(file);
    if (!data) {
      setUploading(false);
      setUploadedPromotionsCount(0);
    }
    setUploadedPromotionsCount(countRulesInGroups(JSON.parse(data)));
    api.uploadPromotions(data);
    setUploading(false);
  };

  return (
    <>
      <UploadDialog
        open={uploadingDialog}
        confirmationTitle={t('export.promotions.confirmation.title')}
        confirmationBody={t('export.promotions.confirmation.body')}
        onUpload={handlePromotionsUpload}
        onClose={() => {
          setUploadingDialog(false);
        }}
        metadataParser={async (file: File) => {
          const data = await readFile(file);
          return {
            'Promotion Groups': String(JSON.parse(data).groups?.length || 0),
            'Promotion Rules': String(countRulesInGroups(JSON.parse(data))),
          };
        }}
      />

      <Paper
        legend={
          <Stack direction="row" gap={1} alignItems="center">
            <PromotionsIcon style={{ fill: 'white' }} />
            <Typography variant="body2">
              {t('export.promotions.panelHeadline')}
            </Typography>
          </Stack>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={handlePromotionsDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              loading={downloading}
              loadingPosition="start"
            >
              {t('export.promotions.downloadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {downloadedPromotionsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.promotions.downloadedHint', {
                      count: downloadedPromotionsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setUploadingDialog(true);
              }}
              startIcon={<UploadIcon />}
              variant="contained"
              loading={uploading}
              loadingPosition="start"
            >
              {t('export.promotions.uploadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {uploadedPromotionsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.promotions.uploadedHint', {
                      count: uploadedPromotionsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>

        <Alert severity="warning" sx={{ marginTop: '16px' }}>
          <Typography variant="body2">
            {t('export.promotions.noShopsHint')}
            <a href={rpLink} target="_blank" rel="noreferrer">
              Retailer Portal
            </a>
          </Typography>
        </Alert>
      </Paper>
    </>
  );
}
